import { Component, type ReactNode, type ErrorInfo } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Card } from './ui/Card'
import { Button } from './ui/Button'

interface Props {
  children: ReactNode
}

interface State {
  error: unknown
  hasError: boolean
  errorInfo: ErrorInfo | null
  detailsOpen: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null, detailsOpen: false }
  }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(_error: unknown, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })
  }

  handleReset() {
    this.setState({ hasError: false, error: null, errorInfo: null, detailsOpen: false })
  }

  handleToggleDetails() {
    this.setState(previous => ({ detailsOpen: !previous.detailsOpen }))
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    const errorMessage =
      this.state.error instanceof Error
        ? this.state.error.message
        : typeof this.state.error === 'string'
          ? this.state.error
          : 'An unexpected error occurred'

    return (
      <div className="flex items-center justify-center min-h-[400px] p-6">
        <Card className="max-w-lg w-full p-8">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
              <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              Something went wrong on this page
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {errorMessage}
            </p>
            <Button
              variant="link"
              size="sm"
              onClick={() => this.handleToggleDetails()}
              aria-expanded={this.state.detailsOpen}
              className="text-xs text-slate-500 dark:text-slate-400"
            >
              {this.state.detailsOpen ? 'Hide details' : 'Show details'}
            </Button>
            {this.state.detailsOpen && this.state.errorInfo && (
              <pre
                className="w-full text-left text-xs bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg p-4 overflow-auto max-h-48"
              >
                {this.state.errorInfo.componentStack}
              </pre>
            )}
            <div className="flex gap-3 mt-2">
              <Button
                onClick={() => this.handleReset()}
                variant="default"
              >
                Try again
              </Button>
              <Button
                asChild
                variant="outline"
              >
                <a href="/app">Go to dashboard</a>
              </Button>
            </div>
          </div>
        </Card>
      </div>
    )
  }
}
